import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';

const TONES = [
  { value: 'friendly', label: 'Amigable', desc: 'Cercano y cálido' },
  { value: 'professional', label: 'Profesional', desc: 'Formal y preciso' },
  { value: 'casual', label: 'Casual', desc: 'Relajado e informal' },
];

const LANG_NAMES = {
  es: 'Español', en: 'English', fr: 'Français',
  de: 'Deutsch', it: 'Italiano', pt: 'Português',
  nl: 'Nederlands', ca: 'Català', eu: 'Euskara', pl: 'Polski',
};

const EXACT_LABEL_DEFAULTS = {
  es: 'Coincidencia exacta', ca: 'Coincidència exacta',
  en: 'Exact match',        fr: 'Correspondance exacte',
  de: 'Genaue Übereinstimmung', it: 'Corrispondenza esatta',
  pt: 'Correspondência exata',  nl: 'Exacte overeenkomst',
  pl: 'Dokładne dopasowanie',   eu: 'Bat etortzea zehatza',
};

export default function CustomizePage() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orgLanguages, setOrgLanguages] = useState([]);

  useEffect(() => {
    api.get('/api/widget').then(r => {
      setConfig(r.data.widget_config);
      setForm(r.data.widget_config);
    });
    // Load detected languages from connector (PrestaShop or Shopify)
    api.get('/api/connectors/prestashop/status').then(r => {
      const langs = r.data?.detected_languages || [];
      if (langs.length > 0) setOrgLanguages(langs);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put('/api/widget', form);
      setConfig(r.data.widget_config);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setForm(f => ({ ...f, [key]: value }));

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-white mb-2">Personalizar widget</h1>
      <p className="text-gray-400 mb-8">Configura la apariencia y el comportamiento del buscador en tu tienda.</p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Config form */}
        <div className="space-y-6">
          {/* Color */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Color principal</h2>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={form.primary_color || '#6366f1'}
                onChange={(e) => update('primary_color', e.target.value)}
                className="w-12 h-12 rounded-lg cursor-pointer border-0 bg-transparent"
              />
              <input
                type="text"
                value={form.primary_color || '#6366f1'}
                onChange={(e) => update('primary_color', e.target.value)}
                placeholder="#6366f1"
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Logo */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-1">Logo de la tienda</h2>
            <p className="text-gray-400 text-xs mb-4">
              URL pública de tu logo. Se mostrará a la izquierda del buscador. Recomendado: PNG/SVG con fondo transparente, máx. 160×38&nbsp;px.
            </p>
            <input
              type="url"
              value={form.logo_url || ''}
              onChange={(e) => update('logo_url', e.target.value)}
              placeholder="https://tutienda.com/logo.png"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
              data-testid="logo-url-input"
            />
            {form.logo_url && (
              <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-700">
                <img
                  src={form.logo_url}
                  alt="Logo preview"
                  className="max-h-10 max-w-[160px] object-contain"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <span className="text-gray-500 text-xs">Vista previa</span>
              </div>
            )}
          </div>

          {/* Assistant name */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Nombre del asistente</h2>
            <input
              type="text"
              value={form.assistant_name || ''}
              onChange={(e) => update('assistant_name', e.target.value)}
              placeholder="Sera"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Tone */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Tono de comunicación</h2>
            <div className="grid grid-cols-3 gap-3">
              {TONES.map(t => (
                <button
                  key={t.value}
                  onClick={() => update('tone', t.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    form.tone === t.value
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                  }`}
                >
                  <p className="text-white text-sm font-medium">{t.label}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Welcome message */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Mensaje de bienvenida</h2>
            <textarea
              value={form.welcome_message || ''}
              onChange={(e) => update('welcome_message', e.target.value)}
              placeholder="¡Hola! ¿En qué puedo ayudarte?"
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 resize-none"
            />
          </div>

          {/* Placeholder */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Texto del placeholder</h2>
            <input
              type="text"
              value={form.placeholder_text || ''}
              onChange={(e) => update('placeholder_text', e.target.value)}
              placeholder="Busca productos..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Exact match label */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-1">Etiqueta de coincidencia exacta</h2>
            <p className="text-gray-400 text-xs mb-4">
              Texto del badge que aparece sobre el producto cuando el usuario busca exactamente su referencia.
              {orgLanguages.length > 1 ? ' Puedes personalizar el texto para cada idioma de tu tienda.' : ' Déjalo vacío para usar el valor por defecto.'}
            </p>
            {orgLanguages.length > 1 ? (
              <div className="space-y-3">
                {orgLanguages.map(lang => {
                  const labelMap = (form.exact_match_label && typeof form.exact_match_label === 'object')
                    ? form.exact_match_label
                    : {};
                  // If stored as string (legacy), pre-fill all langs with that string
                  const legacyVal = (form.exact_match_label && typeof form.exact_match_label === 'string')
                    ? form.exact_match_label : '';
                  const val = labelMap[lang] !== undefined ? labelMap[lang] : legacyVal;
                  return (
                    <div key={lang} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-400 uppercase w-8 flex-shrink-0">{lang}</span>
                      <input
                        type="text"
                        value={val}
                        onChange={(e) => {
                          const current = (form.exact_match_label && typeof form.exact_match_label === 'object')
                            ? { ...form.exact_match_label }
                            : {};
                          update('exact_match_label', { ...current, [lang]: e.target.value });
                        }}
                        placeholder={EXACT_LABEL_DEFAULTS[lang] || 'Exact match'}
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500"
                        data-testid={`exact-match-label-${lang}`}
                      />
                      <span className="text-xs text-gray-500 w-24 flex-shrink-0">{LANG_NAMES[lang] || lang}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <input
                type="text"
                value={typeof form.exact_match_label === 'string' ? form.exact_match_label : ''}
                onChange={(e) => update('exact_match_label', e.target.value)}
                placeholder="Coincidencia exacta"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
                data-testid="exact-match-label-input"
              />
            )}
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-3 rounded-xl font-medium transition-colors"
          >
            {saving ? 'Guardando...' : success ? '✓ Guardado' : 'Guardar configuración'}
          </button>
        </div>

        {/* Preview */}
        <div className="sticky top-8">
          <h2 className="text-white font-semibold mb-4">Vista previa</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            {/* Widget preview */}
            <div className="p-6" style={{ background: '#0a0a0a' }}>
              {/* Search header (mimics widget header) */}
              <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                {form.logo_url ? (
                  <img
                    src={form.logo_url}
                    alt="Logo"
                    className="max-h-8 max-w-[110px] object-contain flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : null}
                <div
                  className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border"
                  style={{
                    borderColor: (form.primary_color || '#6366f1') + '40',
                    background: '#fff',
                  }}
                >
                  <span className="text-gray-400 text-sm">🔍</span>
                  <span className="text-gray-500 text-sm">{form.placeholder_text || 'Busca productos...'}</span>
                </div>
              </div>

              {/* Welcome */}
              <div className="mt-4 p-4 rounded-xl bg-gray-900">
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                    style={{ background: form.primary_color || '#6366f1' }}
                  >
                    {(form.assistant_name || 'S')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">{form.assistant_name || 'Sera'}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{form.welcome_message || '¡Hola! ¿En qué puedo ayudarte?'}</p>
                  </div>
                </div>
              </div>

              {/* Sample results */}
              <p className="text-gray-600 text-xs mt-4 mb-2">Búsquedas populares:</p>
              <div className="flex flex-wrap gap-2">
                {['zapatillas', 'camiseta', 'pantalón'].map(t => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full text-xs"
                    style={{
                      background: (form.primary_color || '#6366f1') + '20',
                      color: form.primary_color || '#6366f1',
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
